import { PayloadAction } from '@reduxjs/toolkit';
import { WritableDraft } from 'immer';

import { QuoteBuilderStateType } from '../../types/state';
import {
  getQuoteLineOrThrow,
  getQuotePriceOrThrow,
} from '../utilities/action-guards';
import { buildIssueId } from '../utilities/issue-id';
import { deleteIssue, upsertIssue } from '../utilities/issue-managers';
import { QuotePriceParams } from './types';

// @TODO(#1684) - For now, we're keeping the deleted quote price until it is confirmed by the API
// We should update the UI to hide the quote price while it is being deleted.
// If deletion fails, we should keep the quote price hidden, but in the state, allowing the user
// to attempt re-deletion.

export function quotePriceDeleted(
  state: WritableDraft<QuoteBuilderStateType>,
  action: PayloadAction<QuotePriceParams>,
) {
  const { quoteLineUuid, quotePriceUuid } = action.payload;

  const quotePrice = getQuotePriceOrThrow(state, quoteLineUuid, quotePriceUuid);

  quotePrice.status = 'deleting';
}

export function quotePriceDeletionSucceeded(
  state: WritableDraft<QuoteBuilderStateType>,
  action: PayloadAction<{ quoteLineUuid: string; quotePriceUuid: string }>,
) {
  const { quoteLineUuid, quotePriceUuid } = action.payload;

  Object.entries(state.issues).forEach(([issueId, issue]) => {
    if (
      issue.area === 'quotePrice' &&
      issue.quotePriceUuid === quotePriceUuid
    ) {
      deleteIssue(state, issueId);
    }
  });

  const quoteLine = getQuoteLineOrThrow(state, quoteLineUuid);

  quoteLine.quotePriceUuids = quoteLine.quotePriceUuids.filter(
    (id) => id !== quotePriceUuid,
  );

  delete quoteLine.quotePrices[quotePriceUuid];
}

export function quotePriceDeletionFailed(
  state: WritableDraft<QuoteBuilderStateType>,
  action: PayloadAction<{ quoteLineUuid: string; quotePriceUuid: string }>,
) {
  const { quoteLineUuid, quotePriceUuid } = action.payload;

  const quotePrice = getQuotePriceOrThrow(state, quoteLineUuid, quotePriceUuid);

  quotePrice.status = 'deletion-failed';

  upsertIssue(
    state,
    buildIssueId({
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      type: 'rejected',
    }),
    {
      title: 'Failed to Delete Price',
      description: 'Quote price could not be deleted in the database',
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      severity: 'error',
      type: 'rejected',
      action: {
        type: 'unrecoverable',
      },
    },
  );
}
